/*----------------------------------
- DOMAINS MEGA MENU
----------------------------------*/

import { TextLink } from '@uniquedomains/components/kit/text-link/TextLink';
import { DOMAINS_MENU_COLUMN_TITLES, type LandingHeaderDomainsNavItem } from '@/client/catalogs/landing/pageContent';
import { LANDING_MENU_PANEL_COPY } from '@/client/catalogs/landing/menuPanels';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import MegaMenuPanel from './MegaMenuPanel';
import MegaMenuSection from './MegaMenuSection';
import MenuCountRow from './MenuCountRow';
import MenuRow from './MenuRow';
import type { MenuNavigateHandler } from './navClick';
import { formatTldLabel, getDomainsMenuColumnItems, readMenuItemCount } from './menuShared';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsMegaMenuProps = {
    id: string;
    isOpen: boolean;
    menu: LandingHeaderDomainsNavItem;
    /** Where a row with no page of its own leads: the screener. */
    screenerHref: string;
    onNavigate: MenuNavigateHandler;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const COPY = LANDING_MENU_PANEL_COPY.domains;

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * Three columns with no head (mock parity web-recheck-01, Gaetan's redraw of 2026-11-02): the lists a visitor can open
 * in two columns of rows, the endings three across (each over its count) with the arrow link to the hub, and the
 * sectors with their counts. Every count is the one Product sent; a count the payload did not carry leaves its row
 * with the label alone rather than printing a zero. "Open the screener" left with the head: "All one-word", the first
 * list, opens the same `/domains`.
 */
export default function DomainsMegaMenu({ id, isOpen, menu, screenerHref, onNavigate }: DomainsMegaMenuProps) {
    const listItems = getDomainsMenuColumnItems(menu, DOMAINS_MENU_COLUMN_TITLES.lists);
    const tldItems = getDomainsMenuColumnItems(menu, DOMAINS_MENU_COLUMN_TITLES.endings);
    const sectorItems = getDomainsMenuColumnItems(menu, DOMAINS_MENU_COLUMN_TITLES.sectors);

    return (
        <MegaMenuPanel id={id} testId="domains-mega-menu" isOpen={isOpen}>
            <MegaMenuSection title={DOMAINS_MENU_COLUMN_TITLES.lists}>
                <ul class="mm-grid">
                    {listItems.map((item) => (
                        <li key={item.label}>
                            <MenuRow
                                href={item.href || screenerHref}
                                label={item.label}
                                description={item.description}
                                iconName={item.iconName}
                                onClick={() => onNavigate(item.label, item.href || screenerHref)}
                            />
                        </li>
                    ))}
                </ul>
            </MegaMenuSection>

            <MegaMenuSection title={DOMAINS_MENU_COLUMN_TITLES.endings}>
                <ul class="mm-ends">
                    {tldItems.map((item) => (
                        <li key={item.label}>
                            <MenuCountRow
                                look="cell"
                                href={item.href || screenerHref}
                                label={formatTldLabel(item.label)}
                                count={readMenuItemCount(item)}
                                ariaLabel={`Open the ${formatTldLabel(item.label)} page`}
                                onClick={() => onNavigate(formatTldLabel(item.label), item.href || screenerHref)}
                            />
                        </li>
                    ))}
                </ul>
                {/* The endings column shows the public set; the hub is where every TLD page is. */}
                <TextLink
                    arrow
                    class="mm-all"
                    href={LANDING_WEBSITE_PAGE_ROUTES.tldHub}
                    onClick={() => onNavigate(COPY.allTldsLabel, LANDING_WEBSITE_PAGE_ROUTES.tldHub)}
                    data-testid="domains-menu-all-tlds"
                >
                    {COPY.allTldsLabel}
                </TextLink>
            </MegaMenuSection>

            <MegaMenuSection title={DOMAINS_MENU_COLUMN_TITLES.sectors}>
                <ul class="mm-counts">
                    {sectorItems.map((item) => (
                        <li key={item.label}>
                            <MenuCountRow
                                href={item.href || screenerHref}
                                label={item.label}
                                count={readMenuItemCount(item)}
                                onClick={() => onNavigate(item.label, item.href || screenerHref)}
                            />
                        </li>
                    ))}
                </ul>
            </MegaMenuSection>
        </MegaMenuPanel>
    );
}
